import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Select, Table, Thead, Tbody, Tr, Th, Td, Button, useToast, Spinner, Text, Flex, useBreakpointValue
} from '@chakra-ui/react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatarMoeda(valor: number) {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatarDataBR(dataStr: string) {
  if (!dataStr) return '';
  // Aceita 'YYYY-MM-DD' e também ISO com hora, cortando o que vem depois
  const [ano, mes, dia] = dataStr.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function formatarDia(dataStr: string) {
  if (!dataStr) return '';
  return dataStr.slice(0, 10).split('-')[2];
}

const FaturasCartao: React.FC = () => {
  const { user } = useAuth();
  const hoje = new Date();
  const [cartoes, setCartoes] = useState<any[]>([]);
  const [cartaoId, setCartaoId] = useState('');
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [lancamentos, setLancamentos] = useState<any[]>([]);
  const [cartao, setCartao] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [diaFechamento, setDiaFechamento] = useState<number | null>(null);
  const [periodoFatura, setPeriodoFatura] = useState<{inicio: string, fim: string} | null>(null);
  const [bancos, setBancos] = useState<any[]>([]);
  const [bancoPagamentoId, setBancoPagamentoId] = useState('');
  const [pagamento, setPagamento] = useState<any>(null);
  const faturaPaga = !!pagamento;

  // Buscar bancos para o pagamento da fatura
  useEffect(() => {
    if (!user) return;
    const fetchBancos = async () => {
      try {
        setBancos(await api.get('/api/bancos'));
      } catch {
        setBancos([]);
      }
    };
    fetchBancos();
  }, [user]);

  // Buscar cartões e dia de fechamento ao selecionar cartão
  useEffect(() => {
    if (!user) return;
    const fetchCartoes = async () => {
      let cartoesData: any[] = [];
      try {
        cartoesData = await api.get('/api/cartoes');
      } catch {
        cartoesData = [];
      }
      setCartoes(cartoesData);
      // Selecionar cartao_id da query string se existir
      const params = new URLSearchParams(location.search);
      const cid = params.get('cartao_id');
      if (cid && cartoesData && cartoesData.find((c: any) => c.id === cid)) {
        setCartaoId(cid);
        setDiaFechamento(cartoesData.find((c: any) => c.id === cid)?.dia_fechamento || null);
      } else if (cartoesData && cartoesData.length > 0 && !cartaoId) {
        setCartaoId(cartoesData[0].id);
        setDiaFechamento(cartoesData[0].dia_fechamento);
      } else if (cartaoId && cartoesData) {
        const cartao = cartoesData.find((c: any) => c.id === cartaoId);
        if (cartao) setDiaFechamento(cartao.dia_fechamento);
      }
    };
    fetchCartoes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  // O mês escolhido é o do VENCIMENTO: cada compra no crédito vive na fatura
  // em que caiu, então a fatura é o conjunto de lançamentos daquele mês.
  // O período de compras é só informativo, calculado de trás pra frente:
  // fatura que vence em M fechou em M (se vence depois do fechamento) ou em
  // M-1 (se vence antes/no mesmo dia)
  useEffect(() => {
    if (diaFechamento == null || !cartao) return;
    const base = dayjs(`${ano}-${String(mes + 1).padStart(2, '0')}-01`);
    const mesFechamento = Number(cartao.dia_vencimento) > Number(diaFechamento)
      ? base
      : base.subtract(1, 'month');
    const fim = mesFechamento.date(Math.min(diaFechamento, mesFechamento.daysInMonth()));
    const anterior = mesFechamento.subtract(1, 'month');
    const inicio = anterior.date(Math.min(diaFechamento, anterior.daysInMonth())).add(1, 'day');
    setPeriodoFatura({
      inicio: inicio.format('YYYY-MM-DD'),
      fim: fim.format('YYYY-MM-DD'),
    });
  }, [mes, ano, diaFechamento, cartao]);

  // Buscar os lançamentos da fatura que vence no mês selecionado
  useEffect(() => {
    if (!user || !cartaoId) return;
    setLoading(true);
    api.get(`/api/lancamentos?cartao_id=${cartaoId}&mes=${mes}&ano=${ano}&ordem=asc`)
      .then(data => setLancamentos(data))
      .catch(() => setLancamentos([]))
      .finally(() => setLoading(false));
  }, [user, cartaoId, mes, ano]);

  // Buscar dados do cartão selecionado (e manter o dia de fechamento em sincronia
  // ao trocar de cartão no select)
  useEffect(() => {
    const c = cartoes.find(c => c.id === cartaoId) || null;
    setCartao(c);
    setDiaFechamento(c ? c.dia_fechamento : null);
  }, [cartoes, cartaoId]);

  // Total da fatura: compras somam, estorno/crédito no cartão subtrai
  const valorFatura = lancamentos.reduce((acc, l) => (
    ['receita', 'transf_entrada', 'repasse_entrada'].includes(l.tipo_lancamento)
      ? acc - Number(l.valor)
      : acc + Number(l.valor)
  ), 0);

  // O mês selecionado é o do vencimento — só falta o dia (limitado ao fim do mês)
  const dataVencimento = (() => {
    if (!cartao) return '';
    const base = dayjs(`${ano}-${String(mes + 1).padStart(2, '0')}-01`);
    return base.date(Math.min(Number(cartao.dia_vencimento), base.daysInMonth())).format('YYYY-MM-DD');
  })();

  const descricaoPagamento = cartao ? `Pagamento fatura ${cartao.nome} ${meses[mes]}/${ano}` : '';

  // A fatura já foi paga? Procura um lançamento de PAGAMENTO DE FATURA perto
  // do vencimento — de qualquer origem. O pagamento normalmente vem do
  // extrato ("PAGAMENTO FATURA CARTAO..."), então procurar pela descrição que
  // esta tela cria deixava passar o pagamento de verdade — e um clique em
  // "Marcar como paga" debitava o banco duas vezes
  useEffect(() => {
    if (!user || !dataVencimento) {
      setPagamento(null);
      return;
    }
    const inicio = dayjs(dataVencimento).subtract(10, 'day').format('YYYY-MM-DD');
    const fim = dayjs(dataVencimento).add(15, 'day').format('YYYY-MM-DD');
    api.get(`/api/lancamentos?data_inicio=${inicio}&data_fim=${fim}`)
      .then((data: any[]) => {
        const pagos = data.filter(l => l.tipo_lancamento === 'pagamento_fatura');
        // Com mais de um cartão, escolhe o pagamento de valor mais próximo
        // do total desta fatura
        const melhor = pagos.sort((a, b) =>
          Math.abs(Number(a.valor) - valorFatura) - Math.abs(Number(b.valor) - valorFatura))[0];
        setPagamento(melhor || null);
      })
      .catch(() => setPagamento(null));
  }, [user, dataVencimento, valorFatura, lancamentos]);

  const diferencaPagamento = pagamento ? Number(pagamento.valor) - valorFatura : 0;

  // Marcar fatura como paga: tipo pagamento_fatura debita do banco escolhido
  // mas fica FORA dos totais de despesa (as compras já contaram quando
  // aconteceram, cada uma na sua categoria); sem cartao_id, pois não é compra
  const handlePagarFatura = async () => {
    if (!cartao || !user) return;
    if (valorFatura <= 0) {
      toast({ title: 'Fatura sem valor a pagar', status: 'warning' });
      return;
    }
    if (!bancoPagamentoId) {
      toast({ title: 'Selecione o banco que pagou a fatura', status: 'warning' });
      return;
    }
    if (faturaPaga) {
      toast({ title: 'Esta fatura já foi marcada como paga', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/lancamentos', {
        categoria_id: null,
        valor: valorFatura,
        data: dataVencimento,
        descricao: descricaoPagamento,
        tipo_lancamento: 'pagamento_fatura',
        cartao_id: null,
        banco_id: bancoPagamentoId,
      });
      toast({ title: 'Fatura marcada como paga!', status: 'success' });
      setPagamento({ valor: valorFatura, data: dataVencimento, descricao: descricaoPagamento });
    } catch (err) {
      toast({ title: 'Erro ao marcar fatura como paga', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  return (
    <Box p={4} maxW="900px" mx="auto">
      <Flex align="center" justify="flex-start" mb={4} gap={2}>
        <Button onClick={() => navigate('/cartoes')} colorScheme="teal" variant="outline" size="sm">Voltar</Button>
        <Heading size="lg" textAlign="center" flex={1}>Faturas do Cartão</Heading>
      </Flex>
      <Flex gap={4} mb={6} align="center" justify="center" direction={{ base: 'column', md: 'row' }}>
        <Box w={{ base: '100%', md: '220px' }}>
          <Select value={cartaoId} onChange={e => setCartaoId(e.target.value)} w="100%">
            {cartoes.map(c => (
              <option value={c.id} key={c.id}>{c.nome}</option>
            ))}
          </Select>
        </Box>
        <Flex gap={4} w={{ base: '100%', md: 'auto' }}>
          <Select value={mes} onChange={e => setMes(Number(e.target.value))} w="100%">
            {meses.map((m, idx) => (
              <option value={idx} key={m}>{m}</option>
            ))}
          </Select>
          <Select value={ano} onChange={e => setAno(Number(e.target.value))} w="100%">
            {[ano-1, ano, ano+1].map(a => (
              <option value={a} key={a}>{a}</option>
            ))}
          </Select>
        </Flex>
      </Flex>
      {loading ? <Spinner /> : (
        <Box>
          <Box mb={6} p={4} borderRadius="md" boxShadow="md" bg="gray.50">
            <Text fontWeight="bold">Valor total da fatura:</Text>
            <Text fontSize="2xl" color="teal.600" fontWeight="bold">R$ {formatarMoeda(valorFatura)}</Text>
            <Text>Vencimento: {formatarDataBR(dataVencimento)}</Text>
            {faturaPaga ? (
              <Box mt={2}>
                <Text color="green.600" fontWeight="bold">
                  Fatura paga ✓ — R$ {formatarMoeda(Number(pagamento.valor))} em{' '}
                  {formatarDataBR(String(pagamento.data))}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {pagamento.descricao || 'pagamento de fatura'}
                </Text>
                {Math.abs(diferencaPagamento) >= 0.01 && (
                  <Text fontSize="sm" color="orange.600" mt={1}>
                    O pagamento é R$ {formatarMoeda(Math.abs(diferencaPagamento))}{' '}
                    {diferencaPagamento > 0 ? 'maior' : 'menor'} que a soma das compras desta
                    fatura — {diferencaPagamento > 0
                      ? 'pode faltar compra no sistema'
                      : 'pode ter compra a mais, ou você pagou parcial'}.
                  </Text>
                )}
              </Box>
            ) : (
              <Flex mt={3} gap={2} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }}>
                <Select
                  placeholder="Banco que pagará a fatura"
                  value={bancoPagamentoId}
                  onChange={e => setBancoPagamentoId(e.target.value)}
                  w={{ base: '100%', md: '260px' }}
                  size="sm"
                >
                  {bancos.map(b => (
                    <option value={b.id} key={b.id}>{b.nome}</option>
                  ))}
                </Select>
                <Button size="sm" colorScheme="teal" onClick={handlePagarFatura} isDisabled={valorFatura <= 0}>
                  Marcar como paga
                </Button>
              </Flex>
            )}
          </Box>
          <Heading size="sm" mb={2}>Compras/Parcelas desta fatura</Heading>
          <Text fontSize="sm" color="gray.500" mb={2}>
            A fatura é o conjunto de compras que vence em {meses[mes]}/{ano} — inclusive
            parcelas de compras antigas.
          </Text>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Data</Th>
                  <Th display={{ base: 'none', md: 'table-cell' }}>Categoria</Th>
                  <Th>Descrição</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {lancamentos.map(lanc => (
                  <Tr key={lanc.id}>
                    {/* Data da compra: no crédito é ela que interessa na fatura */}
                    <Td>{isMobile
                      ? formatarDia(lanc.data_compra || lanc.data)
                      : formatarDataBR(lanc.data_compra || lanc.data)}</Td>
                    <Td display={{ base: 'none', md: 'table-cell' }}>{lanc.categoria?.nome || '-'}</Td>
                    <Td>{lanc.descricao || '-'}</Td>
                    <Td isNumeric color="teal.600">R$ {formatarMoeda(Number(lanc.valor))}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          {periodoFatura && (
            <Text fontSize="sm" color="gray.500" mb={2} textAlign="center">
              Compras de {dayjs(periodoFatura.inicio).format('DD/MM/YYYY')} a {dayjs(periodoFatura.fim).format('DD/MM/YYYY')}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FaturasCartao; 